import { Activation, NextPaymentModalAction } from '~/reducers/user/user.reducer';
import APIAddress from '~/types/APIAddress';
import { Asset, AssetType } from '~/types/Asset';
import Company from '~/types/Company';
import { IDVerification, IDVerificationStatus } from '~/types/IDVerification';
import User, { ProfileUser } from '~/types/User';
import { ActionKeys } from './constants';

export function updateCompany(payload: Company) {
  return <const>{
    payload,
    type: ActionKeys.COMPANY_UPDATE,
  };
}

export function updateCompanySuccess(payload: Company) {
  return <const>{
    payload,
    type: ActionKeys.COMPANY_UPDATE_SUCCESS,
  };
}

export function updateCompanyError() {
  return <const>{
    type: ActionKeys.COMPANY_UPDATE_ERROR,
  };
}

export function fetchCompany() {
  return <const>{
    type: ActionKeys.COMPANY_FETCH,
  };
}

export function fetchCompanySuccess(payload: Company) {
  return <const>{
    payload,
    type: ActionKeys.COMPANY_FETCH_SUCCESS,
  };
}

export function fetchCompanyError() {
  return <const>{
    type: ActionKeys.COMPANY_FETCH_ERROR,
  };
}

export function fetchUser() {
  return <const>{
    type: ActionKeys.USER_FETCH,
  };
}

// TODO: Refactor to remove unnecesary type
export type FetchUserPayload = {
  user: User;
};

export function fetchUserSuccess(payload: FetchUserPayload) {
  return <const>{
    payload,
    type: ActionKeys.USER_FETCH_SUCCESS,
  };
}

export function fetchUserError() {
  return <const>{
    type: ActionKeys.USER_FETCH_ERROR,
  };
}

export function updateFirstLabelPurchased() {
  return <const>{
    type: ActionKeys.USER_FIRST_LABEL_PURCHASED,
  };
}

export function updateUserDefaultServiceLevel(
  defaultProvider: string,
  defaultServiceLevel: string,
) {
  return <const>{
    defaultProvider,
    defaultServiceLevel,
    type: ActionKeys.UPDATE_USER_DEFAULT_SERVICE_LEVEL,
  };
}

export function updateUserDefaultReturnServiceLevel(
  defaultReturnProvider: string,
  defaultReturnServiceLevel: string,
) {
  return <const>{
    defaultReturnProvider,
    defaultReturnServiceLevel,
    type: ActionKeys.UPDATE_USER_DEFAULT_RETURN_SERVICE_LEVEL,
  };
}

// TODO: Refactor to remove unnecesary type
export type UpdateDefaultAddressPayload = {
  address: APIAddress;
};

export function updateUserDefaultAddress(payload: UpdateDefaultAddressPayload) {
  return <const>{
    payload,
    type: ActionKeys.UPDATE_DEFAULT_ADDRESS,
  };
}

export function updateUserDefaultReturnAddress(payload: UpdateDefaultAddressPayload) {
  return <const>{
    payload,
    type: ActionKeys.UPDATE_DEFAULT_RETURN_ADDRESS,
  };
}

export function revokeAuth() {
  return <const>{
    type: ActionKeys.REVOKE_AUTH,
  };
}

export function userSetUnauthenticated() {
  return <const>{
    type: ActionKeys.USER_SET_UNAUTHENTICATED,
  };
}

export function refreshUser() {
  return <const>{
    type: ActionKeys.USER_REFRESH,
  };
}

export function updateCreditCard(elementId, tokenId: string, billingAddress: string) {
  return <const>{
    elementId,
    type: ActionKeys.USER_UPDATE_CREDIT_CARD,
    payload: {
      tokenId,
      billingAddress,
    },
  };
}

export function updateCreditCardSuccess() {
  return <const>{
    type: ActionKeys.USER_UPDATE_CREDIT_CARD_SUCCESS,
  };
}

export function updateCreditCardError(errorMessage: string) {
  return <const>{
    errorMessage,
    type: ActionKeys.USER_UPDATE_CREDIT_CARD_ERROR,
  };
}

export function fetchAssets() {
  return <const>{
    type: ActionKeys.USER_ASSETS_FETCH,
  };
}

export function fetchAssetsSuccess(assets: Asset[]) {
  return <const>{
    type: ActionKeys.USER_ASSETS_FETCH_SUCCESS,
    payload: {
      assets,
    },
  };
}

export function fetchAssetsError(errorCode: string) {
  return <const>{
    type: ActionKeys.USER_ASSETS_FETCH_ERROR,
    payload: {
      errorCode,
    },
  };
}

export function uploadAssetDraft(
  asset: Maybe<Asset>,
  assetType: AssetType,
  rank: number,
  file: File,
) {
  return <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_DRAFT,
    payload: {
      asset,
      assetType,
      rank,
      file,
    },
  };
}

export function uploadAssetDraftSuccess(assetType: AssetType, rank: number) {
  return <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_DRAFT_SUCCESS,
    payload: {
      assetType,
      rank,
    },
  };
}

export function uploadAssetDraftError(assetType: AssetType, rank: number, errorCode: string) {
  return <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_DRAFT_ERROR,
    payload: {
      assetType,
      rank,
      errorCode,
    },
  };
}

export function clearAssetDraft(
  asset: OptionalObjectId<Asset>,
  assetType: AssetType,
  rank: number,
) {
  return <const>{
    type: ActionKeys.USER_ASSET_CLEAR_DRAFT,
    payload: {
      asset,
      assetType,
      rank,
    },
  };
}

export function clearAssetDraftSuccess(assetType: AssetType, rank: number) {
  return <const>{
    type: ActionKeys.USER_ASSET_CLEAR_DRAFT_SUCCESS,
    payload: {
      assetType,
      rank,
    },
  };
}

export function clearAssetDraftError(assetType: AssetType, rank: number, errorCode: string) {
  return <const>{
    type: ActionKeys.USER_ASSET_CLEAR_DRAFT_ERROR,
    payload: {
      assetType,
      rank,
      errorCode,
    },
  };
}

export function revertAssetDraft(
  asset: OptionalObjectId<Asset>,
  assetType: AssetType,
  rank: number,
) {
  return <const>{
    type: ActionKeys.USER_ASSET_REVERT_DRAFT,
    payload: {
      asset,
      assetType,
      rank,
    },
  };
}

export function revertAssetDraftSuccess(assetType: AssetType, rank: number) {
  return <const>{
    type: ActionKeys.USER_ASSET_REVERT_DRAFT_SUCCESS,
    payload: {
      assetType,
      rank,
    },
  };
}

export function revertAssetDraftError(assetType: AssetType, rank: number, errorCode: string) {
  return <const>{
    type: ActionKeys.USER_ASSET_REVERT_DRAFT_ERROR,
    payload: {
      assetType,
      rank,
      errorCode,
    },
  };
}

export function publishAsset(asset: OptionalObjectId<Asset>, assetType: AssetType, rank: number) {
  return <const>{
    type: ActionKeys.USER_ASSET_PUBLISH,
    payload: {
      asset,
      assetType,
      rank,
    },
  };
}

export function publishAssetSuccess(assetType: AssetType, rank: number) {
  return <const>{
    type: ActionKeys.USER_ASSET_PUBLISH_SUCCESS,
    payload: {
      assetType,
      rank,
    },
  };
}

export function publishAssetError(assetType: AssetType, rank: number, errorCode: string) {
  return <const>{
    type: ActionKeys.USER_ASSET_PUBLISH_ERROR,
    payload: {
      assetType,
      rank,
      errorCode,
    },
  };
}

export function uploadAndPublishLogo(asset: Maybe<Asset>, file: File) {
  return <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO,
    payload: {
      asset,
      file,
    },
  };
}

export function uploadAndPublishLogoSuccess() {
  return <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO_SUCCESS,
  };
}

export function uploadAndPublishLogoError(errorCode: string) {
  return <const>{
    type: ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO_ERROR,
    payload: {
      errorCode,
    },
  };
}

export function updateBrandColors(brandPrimaryColor: string, brandSecondaryColor: string) {
  return <const>{
    type: ActionKeys.USER_BRAND_UPDATE_COLORS,
    payload: {
      brandPrimaryColor,
      brandSecondaryColor,
    },
  };
}

export function updateBrandColorsSuccess() {
  return <const>{
    type: ActionKeys.USER_BRAND_UPDATE_COLORS_SUCCESS,
  };
}

export function updateBrandColorsError() {
  return <const>{
    type: ActionKeys.USER_BRAND_UPDATE_COLORS_ERROR,
  };
}

export function setIdVerificationStatus(idVerification: IDVerification) {
  return <const>{
    type: ActionKeys.USER_SET_VERIFICATION_STATUS,
    payload: { idVerification },
  };
}

// Action for the orders page to set this in progress when it loads and the query param is there
export function setIdVerificationStatusInProgress() {
  return setIdVerificationStatus({
    state: IDVerificationStatus.VERIFICATION_PROCESSING,
    errors: [],
  });
}

export function closeVerificationModal() {
  return <const>{
    type: ActionKeys.USER_CLOSE_VERIFICATION_MODAL,
  };
}

export function initiateVerification() {
  return <const>{
    type: ActionKeys.USER_INITIATE_ID_VERIFICATION,
  };
}

export function updateActivationData(activation: Activation) {
  return <const>{
    type: ActionKeys.UPDATE_ACTIVATION_DATA,
    payload: activation,
  };
}

export function showPromoApplicationNotice() {
  return <const>{
    type: ActionKeys.SHOW_PROMO_APPLICATION_NOTICE,
  };
}

export function dismissPromoApplicationNotice() {
  return <const>{
    type: ActionKeys.DISMISS_PROMO_APPLICATION_NOTICE,
  };
}

export function updateUser(user: ProfileUser) {
  return <const>{
    type: ActionKeys.USER_UPDATE,
    payload: {
      user,
    },
  };
}
export function addUser(email, callback) {
  return <const>{
    type: ActionKeys.USER_ADD,
    payload: {
      email: email,
    },
    callback,
  };
}

export function getLoginUsers(callback) {
  return <const>{
    type: ActionKeys.USER_ALL_FETCH,
    callback,
  };
}

export function resendInvite(callback, id) {
  return <const>{
    type: ActionKeys.USER_RESEND_INVITE,
    callback,
    payload: {
      userId: id,
    },
  };
}

export function deleteUser(callback, id) {
  return <const>{
    type: ActionKeys.USER_DELETE,
    callback,
    payload: {
      userId: id,
    },
  };
}

export function updateUserSuccess(user: ProfileUser) {
  return <const>{
    type: ActionKeys.USER_UPDATE_SUCCESS,
    payload: {
      user,
    },
  };
}

export function updateUserError(error: string) {
  return <const>{
    type: ActionKeys.USER_UPDATE_ERROR,
    payload: {
      error,
    },
  };
}

export function maybeShowEmailVerificationModal(nextModalAction: NextPaymentModalAction) {
  return <const>{
    type: ActionKeys.MAYBE_SHOW_EMAIL_VERIFICATION_MODAL,
    payload: {
      nextModalAction,
    },
  };
}

export function showEmailVerificationModal(nextModalAction: NextPaymentModalAction) {
  return <const>{
    type: ActionKeys.SHOW_EMAIL_VERIFICATION_MODAL,
    payload: {
      nextModalAction,
    },
  };
}

export function closeEmailVerificationModal() {
  return <const>{
    type: ActionKeys.CLOSE_EMAIL_VERIFICATION_MODAL,
  };
}

export function emailVerificationSuccess() {
  return <const>{
    type: ActionKeys.USER_EMAIL_VERIFICATION_SUCCESS,
  };
}

export function checkRequiresEmailVerification(verifySuccessCallback: () => void) {
  return <const>{
    type: ActionKeys.CHECK_REQUIRES_EMAIL_VERIFICATION,
    payload: {
      verifySuccessCallback,
    },
  };
}

export type UserActions =
  | ReturnType<typeof resendInvite>
  | ReturnType<typeof deleteUser>
  | ReturnType<typeof getLoginUsers>
  | ReturnType<typeof updateCompany>
  | ReturnType<typeof updateUserSuccess>
  | ReturnType<typeof updateUserError>
  | ReturnType<typeof updateUser>
  | ReturnType<typeof addUser>
  | ReturnType<typeof updateCompanySuccess>
  | ReturnType<typeof updateCompanyError>
  | ReturnType<typeof fetchCompany>
  | ReturnType<typeof fetchCompanySuccess>
  | ReturnType<typeof fetchCompanyError>
  | ReturnType<typeof fetchUser>
  | ReturnType<typeof fetchUserSuccess>
  | ReturnType<typeof fetchUserError>
  | ReturnType<typeof updateUserDefaultServiceLevel>
  | ReturnType<typeof updateUserDefaultReturnServiceLevel>
  | ReturnType<typeof updateUserDefaultAddress>
  | ReturnType<typeof updateUserDefaultReturnAddress>
  | ReturnType<typeof revokeAuth>
  | ReturnType<typeof userSetUnauthenticated>
  | ReturnType<typeof refreshUser>
  | ReturnType<typeof updateCreditCard>
  | ReturnType<typeof updateCreditCardSuccess>
  | ReturnType<typeof updateCreditCardError>
  | ReturnType<typeof fetchAssets>
  | ReturnType<typeof fetchAssetsSuccess>
  | ReturnType<typeof fetchAssetsError>
  | ReturnType<typeof uploadAssetDraft>
  | ReturnType<typeof uploadAssetDraftSuccess>
  | ReturnType<typeof uploadAssetDraftError>
  | ReturnType<typeof clearAssetDraft>
  | ReturnType<typeof clearAssetDraftSuccess>
  | ReturnType<typeof clearAssetDraftError>
  | ReturnType<typeof revertAssetDraft>
  | ReturnType<typeof revertAssetDraftSuccess>
  | ReturnType<typeof revertAssetDraftError>
  | ReturnType<typeof publishAsset>
  | ReturnType<typeof publishAssetSuccess>
  | ReturnType<typeof publishAssetError>
  | ReturnType<typeof uploadAndPublishLogo>
  | ReturnType<typeof uploadAndPublishLogoSuccess>
  | ReturnType<typeof uploadAndPublishLogoError>
  | ReturnType<typeof updateBrandColors>
  | ReturnType<typeof updateBrandColorsSuccess>
  | ReturnType<typeof updateBrandColorsError>
  | ReturnType<typeof setIdVerificationStatus>
  | ReturnType<typeof closeVerificationModal>
  | ReturnType<typeof initiateVerification>
  | ReturnType<typeof updateActivationData>
  | ReturnType<typeof showPromoApplicationNotice>
  | ReturnType<typeof dismissPromoApplicationNotice>
  | ReturnType<typeof updateFirstLabelPurchased>
  | ReturnType<typeof maybeShowEmailVerificationModal>
  | ReturnType<typeof showEmailVerificationModal>
  | ReturnType<typeof closeEmailVerificationModal>
  | ReturnType<typeof emailVerificationSuccess>;
